import React, {
  Fragment,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import MainLayout from "../components/layouts/MainLayout";
import DoorlockTileList from "../components/views/doorlocks/DoorlockTileList";
import Spin from "../components/Spin";
import { doorlockTileListAction } from "../stores/actions";
import * as api from "../api";
import * as nwdlApi from "../api/nwdl";
import useDebounce from "../lib/useDebounce";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useMqttClient } from "../hooks/useMqttClient";

const DoorlockTileListContainer = ({ history }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDebounce, setIsDebounce] = useState(false); //디바운싱 사용 구분을 위해 추가
  const hotelId = localStorage.getItem("hotel_id");
  const [buildingListItems, setBuildingListItems] = useState([]);
  const [floorListItems, setFloorListItems] = useState([]);
  const [detailModal, setDetailModal] = useState(false); // Doorlock Detail modal
  const [roomDetail, setRoomDetail] = useState();
  const [roomItem, setRoomItem] = useState();
  const [logsModal, setLogsModal] = useState(false); // 상태로그/제어로그 모달창
  const [logTypes, setLogTypes] = useState(""); // 상태로그/제어로그 타입
  const [checkoutChangeModal, setCheckoutChangeModal] = useState(false); // 체크아웃 변경 모달창
  const [moveRoomCardKeyModal, setMoveRoomCardKeyModal] = useState(false); // 객실 이동 (카드키) 모달창
  const [changeRoomStatusModal, setChangeRoomStatusModal] = useState(false); // 객실 상태 변경 모달창

  const [moveRoomPinCodeModal, setMoveRoomPinCodeModal] = useState(false); // 객실 이동 (핀코드) 모달창

  const [logFilter, setLogFilter] = useState({
    startAt: moment().startOf("day").toDate(),
    endAt: moment().endOf("day").toDate(),
  });

  const [statusLogs, setStatusLogs] = useState([]); // 상태 로그 데이터

  const [data0, setData0] = useState("");
  const [data1, setData1] = useState("");
  const [data2, setData2] = useState("");
  const [data3, setData3] = useState("");

  const [frameNumber, setFrameNumber] = useState(); // 상태, 제어 프레임 넘버 데이터

  const [mqttModal, setMqttModal] = useState(false); // mqtt test 모달창

  // 모달창 열리면 10초마다 갱신
  const detailIntervalRef = useRef(null);
  const logsIntervalRef = useRef(null);

  const { doorlockListTotal, doorlockListItems, paginationItem, filterItem } =
    useSelector((state) => state.doorlockTileList);

  const dispatch = useDispatch();

  const { t } = useTranslation(); //i18N 다국어 부분 (locales 폴더 참조)

  // mqtt 클라이언트 커스텀훅 사용
  const { sendMessage, connected, reconnect } = useMqttClient();

  const setDoorlockList = useCallback(
    (doorlockList) => {
      dispatch(doorlockTileListAction.setDoorlockList(doorlockList)); //도어락 데이터 상태를 전역으로 저장 (리덕스 썽크 참조.)
    },
    [dispatch]
  );

  const setFilterItem = useCallback(
    (filterItem, isDebounce) => {
      setIsDebounce(isDebounce); //디바운싱 처리를 위해 하위 Component에서 구분을 받아옴
      dispatch(doorlockTileListAction.setFilterItem(filterItem));
    },
    [dispatch]
  );

  const changeHotel = useCallback(() => {
    dispatch(doorlockTileListAction.initDoorlockListState());
  }, [dispatch]);

  const listBuilding = useCallback(async () => {
    try {
      const res = await api.listBuilding({});
      setBuildingListItems(res.data.items); //빌딩 데이터를 프론트 일반 useState 상태로 저장함.
    } catch (error) {
      throw error;
    }
  }, []);

  const listFloor = useCallback(async () => {
    try {
      if (!filterItem.buildingId) {
        setFloorListItems([]);
      }
      const res = await api.listFloor({ buildingId: filterItem.buildingId });
      setFloorListItems(res.data.items); //층 데이터를 프론트 일반 useState 상태로 저장함.
    } catch (error) {
      throw error;
    }
  }, [filterItem]);

  /* 
  API Title		  네트워크 도어락 목록 조회
  API Path		  /hotels/:hotel_id/network-doorlocks
  API Method	  GET
  API Change    listDoorlock -> listNetworkDoorLock
  UPDATE DATE   2025.06.19
  AUTHOR        LUKA
  */
  const listNetworkDoorLock = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await nwdlApi.listNetworkDoorLock({
        ...filterItem,
        ...paginationItem,
      });
      setDoorlockList(res.data); //도어락 데이터를 프론트 리덕스 상태로 저장함.
    } catch (error) {
      setDoorlockList({
        total: 0,
        items: [],
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [setDoorlockList, filterItem, paginationItem]);

  /* 
  API Title		  네트워크 도어락 상세 조회
  API Path		  /hotels/:hotel_id/network-doorlocks/:doorlock_id
  API Method	  GET
  UPDATE DATE   2025.06.19
  AUTHOR        LUKA
  */
  const listDetailNetworkDoorLock = useCallback(async (doorlock_id) => {
    try {
      setIsLoading(true);

      const res = await nwdlApi.listDetailNetworkDoorLock({ doorlock_id });
      setRoomItem(res.data);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* 
  API Title		  네트워크 도어락 상태 제어 프레임 넘버 조회
  API Path		  /hotels/:hotel_id/network-doorlocks/:doorlock_id/status-control-frame_number
  API Method	  GET
  UPDATE DATE   2025.07.24
  AUTHOR        LUKA
  */
  const listDetailNetworkDoorFrameNumber = useCallback(async (doorlock_id) => {
    try {
      setIsLoading(true);

      const res = await nwdlApi.listDetailNetworkDoorFrameNumber({
        doorlock_id,
      });
      setFrameNumber(res.data);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  //디바운싱 훅으로 이동
  const handleDebounce = useCallback(
    useDebounce((listNetworkDoorLock) => listNetworkDoorLock(), 300), //0.3초 동안 미입력 시 함수 실행
    []
  );

  // 모달창 Open
  const handleDetailModalOpen = (index) => {
    setDetailModal(true);
    setRoomDetail(doorlockListItems[index]);
  };

  // 모달창 Close
  const handleDetailModalClose = () => {
    setDetailModal(false);
  };

  // 상태 로그/ 제어 로그 버튼 모달창 Open
  const handleLogsModalOpen = async (type) => {
    setDetailModal(false);
    setLogsModal(true);
    setLogTypes(type);

    // 오늘 날짜 00:00:00
    const startAt = moment().startOf("day").utc().toISOString(); // ex: 2025-06-18T00:00:00.000Z
    // 오늘 날짜 23:59:59.999
    const endAt = moment().endOf("day").utc().toISOString(); // ex: 2025-06-18T23:59:59.999Z

    // 상태 로그 조회
    // TODO: start_at, end_at 옵션도 넣어야 함
    if (type === t("doorlock_detail_modal.status_logd") && roomDetail?.id) {
      try {
        const res = await nwdlApi.getStatusLogs({
          doorlockId: roomDetail.id,
          startAt: startAt,
          endAt: endAt,
        });
        setStatusLogs(res.data.items);
      } catch (error) {
        setStatusLogs([]);
        throw error;
      }
    }

    // 제어 로그 조회
    if (type === t("doorlock_detail_modal.control_logd") && roomDetail?.id) {
      try {
        const res = await nwdlApi.getControlLogs({
          doorlockId: roomDetail.id,
          startAt: startAt,
          endAt: endAt,
        });
        setStatusLogs(res.data.items);
      } catch (error) {
        setStatusLogs([]);
        throw error;
      }
    }
  };

  // 상태 로그 / 제어 로그 버튼 모달창 Close
  const handleLogsModalClose = () => {
    setLogsModal(false);
    setDetailModal(true);
  };

  // 체크아웃 변경 버튼 모달창 Open
  const handleCheckOutChangeModalOpen = () => {
    setDetailModal(false);
    setCheckoutChangeModal(true);
    setData1("1");
  };

  // 체크아웃 변경 버튼 모달창 Close
  const handleCheckOutChangeModalClose = () => {
    setCheckoutChangeModal(false);
    setDetailModal(true);
    setData1();
  };

  // 객실 이동 (카드키) 버튼 모달창 Open
  const handleMoveRoomCardKeyModalOpen = () => {
    setDetailModal(false);
    setMoveRoomCardKeyModal(true);
    setData3(doorlockListItems[0].card_guest_seq_num);
  };

  // 객실 이동 (카드키) 버튼 모달창 Close
  const handleMoveRoomCardKeyModalClose = () => {
    setMoveRoomCardKeyModal(false);
    setDetailModal(true);
    setData3();
  };

  // 객실 상태 변경 버튼 모달창 Open
  const handleChangeRoomStatusModalOpen = () => {
    setDetailModal(false);
    setChangeRoomStatusModal(true);
    setData0("00");
  };

  // 객실 상태 변경 버튼 모달창 Close
  const handleChangeRoomStatusModalClose = () => {
    setChangeRoomStatusModal(false);
    setDetailModal(true);
    setData0();
  };

  // 객실 이동 (핀코드) 버튼 모달창 Open
  const handleMoveRoomPinCodeModalOpen = () => {
    setDetailModal(false);
    setMoveRoomPinCodeModal(true);
    setData0("123456");
    setData1("789012");
    setData2(Math.floor(Date.now() / 1000));
    setData3(Math.floor(Date.now() / 1000));
  };

  // 객실 이동 (핀코드) 버튼 모달창 Close
  const handleMoveRoomPinCodeModalClose = () => {
    setMoveRoomPinCodeModal(false);
    setDetailModal(true);
    setData0();
    setData1();
    setData2();
    setData3();
  };

  // frame 카운터 업데이트 핸들러
  const handleFrameCount = async () => {
    if (!roomDetail?.id) return;
    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
      // 성공 알림 등 추가 가능
      alert("카운터 업데이트 완료");
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("카운터 업데이트 실패");
    }
  };

  // 상태로그/제어로그 날짜 필터 핸들러
  const handleLogFilterChange = useCallback((targetItem) => {
    setLogFilter((prev) => ({
      ...prev,
      ...targetItem,
    }));
  }, []);

  // 상태로그/제어로그 날짜 초기값 세팅
  const initLogFilter = useCallback(() => {
    setLogFilter({
      startAt: moment().startOf("day").toDate(),
      endAt: moment().endOf("day").toDate(),
    });
  }, []);

  // 필터 핸들러 api 호출
  const handleLogsWithCurrentFilter = async () => {
    if (!roomDetail?.id) return;
    const startAt = moment(logFilter.startAt).utc().toISOString();
    const endAt = moment(logFilter.endAt).utc().toISOString();

    try {
      if (logTypes === t("doorlock_detail_modal.status_logd")) {
        const res = await nwdlApi.getStatusLogs({
          doorlockId: roomDetail.id,
          startAt,
          endAt,
        });
        setStatusLogs(res.data.items);
      }
      if (logTypes === t("doorlock_detail_modal.control_logd")) {
        const res = await nwdlApi.getControlLogs({
          doorlockId: roomDetail.id,
          startAt,
          endAt,
        });
        setStatusLogs(res.data.items);
      }
    } catch (error) {
      setStatusLogs([]);
      // 필요 시 에러처리
    }
  };

  // 강제 개방 버튼 핸들러
  const handleForcedOpening = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // 01: Door lock Open
    // 81: Emergence Open
    // F1: Emergence Clear

    // data1
    // dummy: 0

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 76,
      data0: "01",
      data1: "0",
      data2: "",
      data3: "",
      data4: "",
    };
    sendMessage(payload);

    if (!roomDetail?.id) return;
    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("도어 오픈 명령 실패");
    }

    alert("도어 오픈 명령이 전송되었습니다!");
  };

  // 도어락 시간 설정 버튼 핸들러
  const handleSetDoorLockTime = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // Unix Epoch Time
    // https://www.epochconverter.com/

    // Math.floor(Date.now() / 1000)

    // 예시)
    // Unix timestamp
    // "Tue, 17 Jun 2025 04:55:21 GMT"
    // = "1750136121"

    // https://ko.wikipedia.org/wiki/ISO_8601
    // 2016-10-27T17:13:40+00:00

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 84,
      data0: String(Math.floor(Date.now() / 1000)),
      data1: "",
      data2: "",
      data3: "",
      data4: "",
    };
    sendMessage(payload);

    if (!roomDetail?.id) return;
    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("도어락 시간 설정 실패");
    }

    alert("도어락 시간 설정 명령이 전송되었습니다!");
  };

  // 락아웃 (카드키) 변경 버튼 핸들러
  const handleChangeDoorLockSequenceNumber = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index,
    card_guest_seq_num,
    room_id
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // C3: Card Guest

    // data1
    // sequence_number  (0 ~ 255) -> common_room(table)-> card_guest_seq_num(column)

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 83,
      data0: "C3",
      data1: String(Number(card_guest_seq_num) + 1),
      data2: "",
      data3: "",
      data4: "",
    };
    sendMessage(payload);

    if (!roomDetail?.id || !room_id) return;

    try {
      console.log("roomDetail :::", roomDetail);
      await nwdlApi.updateCardGuestSeqNum({ room_id: room_id });

      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("락아웃 (카드키) 변경 실패");
    }

    alert("락아웃 (카드키) 명령이 전송되었습니다!");
  };

  // 객실 이동 (카드키) 버튼 핸들러 (A 객실 -> B 객실 이동)
  const handleMoveRoomCardKey = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index,
    select_building_index,
    select_floor_index,
    select_room_index,
    select_card_guest_seq_num
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }

    // A 객실 -> B 객실 이동 시나리오
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // B객실 building_index (0 ~ 255)

    // data1
    // B객실 floor_index (0 ~ 255)

    // data2
    // B객실 room_index (0 ~ 255)

    // data3
    // B객실 sequence_number (0 ~ 255)

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 82,
      data0: String(select_building_index),
      data1: String(select_floor_index),
      data2: String(select_room_index),
      data3: String(select_card_guest_seq_num),
      data4: "",
    };

    sendMessage(payload);

    if (!roomDetail?.id) return;
    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("객실 이동 (카드키) 명령 실패");
    }

    alert("객실 이동 (카드키) 명령이 전송되었습니다!");
  };

  // 체크아웃 시간 연장 버튼 핸들러
  const handleExtensionOfCheckOutTime = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // 00: 시간 옵셋

    // data1
    // 시간 옵셋: 1 ~ 32760 (1은 1시간 단위)

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 79,
      data0: "00",
      data1: String(data1),
      data2: "",
      data3: "",
      data4: "",
    };
    sendMessage(payload);

    if (!roomDetail?.id) return;
    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("체크아웃 시간 연장 실패");
    }

    alert("체크아웃 시간 연장 명령이 전송되었습니다!");
  };

  // 객실 상태 변경 버튼 핸들러
  const handleChangeRoomStatus = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // 00: 운영 대기
    // 01: 체크인 (입실)
    // 02: 체크아웃
    // F0: 청소 대기
    // F1: 청소 중

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 67,
      data0: String(data0),
      data1: "",
      data2: "",
      data3: "",
      data4: "",
    };
    sendMessage(payload);

    if (!roomDetail?.id) return;
    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("객실 상태 변경 실패");
    }

    alert("객실 상태 변경 명령이 전송되었습니다!");
  };

  // 객실 이동 (핀코드) 버튼 핸들러
  const handleMoveRoomPinCode = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // A객실 핀코드 현) R & D로 핀코드는 존재한다고 생각하고 Input으로 직접 입력하여 데이터 보내기

    // data1
    // B객실 핀코드

    // data2
    // 핀코드 시작 시간 * '[2] 도어락 시간 설정' DATA0 형식 참고

    // data3
    // 핀코드 만료 시간 * '[2] 도어락 시간 설정' DATA0 형식 참고

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 80,
      data0: String(data0),
      data1: String(data1),
      data2: String(data2),
      data3: String(data3),
      data4: "",
    };

    sendMessage(payload);

    if (!roomDetail?.id) return;

    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("객실 이동 (핀코드) 실패");
    }

    alert("객실 이동 (핀코드) 명령이 전송되었습니다!");
  };

  // 락아웃 (핀코드) 핸들러
  const handleChangeLockOutPinCode = async (
    dl_serial,
    frame_number,
    building_index,
    floor_index,
    room_index,
    pincode_v3_guest_seq_num,
    room_id
  ) => {
    if (!connected) {
      alert("MQTT 연결이 안되어있습니다.");
      return;
    }
    // 필요한 구조에 맞춰 mqtt publish
    // data0
    // CC: PinCode (V3, V4)

    // data1
    // codebook_index: 7 (0 ~ 7)
    // *** 현재는 고정값 (7)
    // XXX hardcode
    // 설정 가능하나 키서버는 고정값 (7)을 사용중.

    // data2
    // room_v2.pincode_v3_guest_seq_num (0 ~ 1000)
    // *** BookingModel._getNextSeqNum(Number(room.pincode_v3_guest_seq_num), 'pincodeV3')

    const payload = {
      dl_serial,
      building_index,
      floor_index,
      room_index,
      frame_number,
      code: 83,
      data0: String("CC"),
      data1: String("7"),
      data2: String((pincode_v3_guest_seq_num + 1) % 1001),
      data3: "",
      data4: "",
    };

    sendMessage(payload);

    if (!roomDetail?.id || !room_id) return;
    try {
      await nwdlApi.updateFrameCount({ doorlock_id: roomDetail.id });

      await nwdlApi.updatePinGuestSeqNum({ room_id: room_id });

      await nwdlApi.listDetailNetworkDoorLock({ doorlock_id: roomDetail.id });
    } catch (error) {
      // 에러 알림 등 추가 가능
      alert("락아웃 (핀코드) 실패");
    }

    alert("락아웃 (핀코드) 명령이 전송되었습니다!");
  };

  // Mqtt 테스트 핸들러
  const handleMqttModalOpen = () => {
    setDetailModal(false);
    setMqttModal(true);
  };

  // Mqtt 테스트 핸들러
  const handleMqttModalClose = () => {
    setMqttModal(false);
    setDetailModal(true);
  };

  // Mqtt 수동 재연결 핸들러
  const handleMqttReconnect = () => {
    reconnect();
    alert("MQTT 연결을 다시 시도합니다.");
  };

  useEffect(() => {
    try {
      listBuilding(); //빌딩 API 호출
    } catch (error) {
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  useEffect(() => {
    try {
      if (filterItem.buildingId) listFloor(); //층 API 호출
    } catch (error) {
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterItem.buildingId]);

  useEffect(() => {
    try {
      //도어락 목록 API 호출
      !isDebounce ? listNetworkDoorLock() : handleDebounce(listNetworkDoorLock); //디바운싱 처리
    } catch (error) {
      throw error;
    }
  }, [handleDebounce, isDebounce, listNetworkDoorLock]);

  // 네트워크 도어락 상세조회 API 호출
  useEffect(() => {
    if (detailModal && roomDetail?.id) {
      listDetailNetworkDoorLock(roomDetail.id);
    }
  }, [detailModal, roomDetail, listDetailNetworkDoorLock]);

  // 네트워크 도어락 상태 제어 프레임 넘버 호출
  useEffect(() => {
    if (detailModal && roomDetail?.id) {
      listDetailNetworkDoorFrameNumber(roomDetail.id);
    }
  }, [detailModal, roomDetail, listDetailNetworkDoorFrameNumber]);

  // detailModal이 true일 때 10초마다 상세조회 API 호출
  useEffect(() => {
    // detailModal이 true이고 roomDetail이 있으면 interval 시작
    if (detailModal && roomDetail?.id) {
      // 즉시 한 번 실행 (바로 갱신)
      listDetailNetworkDoorLock(roomDetail.id);
      listDetailNetworkDoorFrameNumber(roomDetail.id);

      detailIntervalRef.current = setInterval(() => {
        listDetailNetworkDoorLock(roomDetail.id);
        listDetailNetworkDoorFrameNumber(roomDetail.id);
      }, 10000); // 10초마다
    } else {
      // detailModal이 닫히거나 roomDetail이 없으면 interval 해제
      if (detailIntervalRef.current) {
        clearInterval(detailIntervalRef.current);
        detailIntervalRef.current = null;
      }
    }

    // 언마운트 시에도 interval 해제
    return () => {
      if (detailIntervalRef.current) {
        clearInterval(detailIntervalRef.current);
        detailIntervalRef.current = null;
      }
    };
  }, [
    detailModal,
    roomDetail,
    listDetailNetworkDoorLock,
    listDetailNetworkDoorFrameNumber,
  ]);

  useEffect(() => {
    // logsModal이 열리고, roomDetail과 logTypes가 세팅된 경우
    if (logsModal && roomDetail?.id && logTypes) {
      const startAt = moment().startOf("day").utc().toISOString();
      const endAt = moment().endOf("day").utc().toISOString();

      // 한 번 즉시 실행
      if (logTypes === t("doorlock_detail_modal.status_logd")) {
        nwdlApi
          .getStatusLogs({
            doorlockId: roomDetail.id,
            startAt,
            endAt,
          })
          .then((res) => setStatusLogs(res.data.items))
          .catch(() => setStatusLogs([]));
      }
      if (logTypes === t("doorlock_detail_modal.control_logd")) {
        nwdlApi
          .getControlLogs({
            doorlockId: roomDetail.id,
            startAt,
            endAt,
          })
          .then((res) => setStatusLogs(res.data.items))
          .catch(() => setStatusLogs([]));
      }

      // 10초마다 반복
      logsIntervalRef.current = setInterval(() => {
        if (logTypes === t("doorlock_detail_modal.status_logd")) {
          nwdlApi
            .getStatusLogs({
              doorlockId: roomDetail.id,
              startAt,
              endAt,
            })
            .then((res) => setStatusLogs(res.data.items))
            .catch(() => setStatusLogs([]));
        }
        if (logTypes === t("doorlock_detail_modal.control_logd")) {
          nwdlApi
            .getControlLogs({
              doorlockId: roomDetail.id,
              startAt,
              endAt,
            })
            .then((res) => setStatusLogs(res.data.items))
            .catch(() => setStatusLogs([]));
        }
      }, 10000); // 10초
    } else {
      if (logsIntervalRef.current) {
        clearInterval(logsIntervalRef.current);
        logsIntervalRef.current = null;
      }
    }

    // 언마운트시 interval 해제
    return () => {
      if (logsIntervalRef.current) {
        clearInterval(logsIntervalRef.current);
        logsIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsModal, roomDetail, logTypes]);

  useEffect(() => {}, [data0, data1, data2, data3]);

  return (
    <Fragment>
      <Spin isSpin={isLoading} />
      <MainLayout
        history={history}
        refresh={changeHotel}
        ContentBody={
          <Fragment>
            {/* 도어락 타일 목록 UI 부분 Import */}
            <DoorlockTileList
              doorlockListTotal={doorlockListTotal}
              doorlockListItems={doorlockListItems}
              buildingListItems={buildingListItems}
              floorListItems={floorListItems}
              filterItem={filterItem}
              detailModal={detailModal}
              roomDetail={roomDetail}
              logsModal={logsModal}
              checkoutChangeModal={checkoutChangeModal}
              logTypes={logTypes}
              statusLogs={statusLogs}
              logFilter={logFilter}
              roomItem={roomItem}
              mqttModal={mqttModal} // mqtt test
              moveRoomCardKeyModal={moveRoomCardKeyModal}
              changeRoomStatusModal={changeRoomStatusModal}
              moveRoomPinCodeModal={moveRoomPinCodeModal}
              data0={data0}
              data1={data1}
              data2={data2}
              data3={data3}
              frameNumber={frameNumber}
              connected={connected}
              setFilterItem={setFilterItem}
              handleDetailModalOpen={handleDetailModalOpen}
              handleDetailModalClose={handleDetailModalClose}
              handleLogsModalOpen={handleLogsModalOpen}
              handleLogsModalClose={handleLogsModalClose}
              handleCheckOutChangeModalOpen={handleCheckOutChangeModalOpen} // 체크아웃 시간 연장
              handleCheckOutChangeModalClose={handleCheckOutChangeModalClose} // 체크아웃 시간 연장
              handleFrameCount={handleFrameCount}
              handleLogFilterChange={handleLogFilterChange}
              initLogFilter={initLogFilter}
              handleLogsWithCurrentFilter={handleLogsWithCurrentFilter}
              handleForcedOpening={handleForcedOpening} // 도어 오픈
              handleSetDoorLockTime={handleSetDoorLockTime} // 도어락 시간 설정
              handleChangeDoorLockSequenceNumber={
                handleChangeDoorLockSequenceNumber
              } // 도어락 시퀀스 넘버 변경
              handleMoveRoomCardKeyModalOpen={handleMoveRoomCardKeyModalOpen} // 객실 이동 (카드키)
              handleMoveRoomCardKeyModalClose={handleMoveRoomCardKeyModalClose} // 객실 이동 (카드키)
              handleExtensionOfCheckOutTime={handleExtensionOfCheckOutTime} // 체크아웃 시간 연장
              handleMoveRoomCardKey={handleMoveRoomCardKey} // 객실 이동 (카드키)
              handleChangeRoomStatus={handleChangeRoomStatus} // 객실 상태 변경
              handleChangeRoomStatusModalOpen={handleChangeRoomStatusModalOpen} // 객실 상태 변경
              handleChangeRoomStatusModalClose={
                handleChangeRoomStatusModalClose
              } // 객실 상태 변경
              handleMoveRoomPinCodeModalOpen={handleMoveRoomPinCodeModalOpen} // 객실 이동 (핀코드)
              handleMoveRoomPinCodeModalClose={handleMoveRoomPinCodeModalClose} // 객실 이동 (핀코드)
              handleMoveRoomPinCode={handleMoveRoomPinCode} // 객실 이동 (핀코드)
              handleChangeLockOutPinCode={handleChangeLockOutPinCode} // 락아웃 (핀코드)
              handleMqttModalOpen={handleMqttModalOpen} // mqtt test
              handleMqttModalClose={handleMqttModalClose} // mqtt test
              setData0={setData0}
              setData1={setData1}
              setData2={setData2}
              setData3={setData3}
              handleMqttReconnect={handleMqttReconnect}
            />
          </Fragment>
        }
      />
    </Fragment>
  );
};

export default DoorlockTileListContainer;
